<script setup lang="ts">
import type { ImageAndMaybeText } from '~/models/Presentation/Pages/ImageAndMaybeText'
import type { BasePageProps } from '~/models/Presentation/BasePage'
import type { ContentVideo } from '~/models/Content/ContentVideo'
import { useI18n } from 'vue-i18n'
import { computed, ref, useTemplateRef } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import useImage from '~/composables/useImage'
import useHiddenHeading from '~/composables/useHiddenHeading'
import useContentApi from '~/api/contentApi'
import Button from '~/components/ui/Button.vue'
import LottiePlayer from '~/components/media/LottiePlayer.vue'
import ImageWithCaption from '~/components/media/ImageWithCaption.vue'

const props = defineProps<BasePageProps & { page: ImageAndMaybeText }>()

const textPositions: Record<string, string> = {
  'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center',
  'top-center': 'top-16 left-1/2 -translate-x-1/2 text-center',
  'bottom-center': 'bottom-32 left-1/2 -translate-x-1/2 text-center',
  'top-left': 'top-16 left-16 text-left',
  'top-right': 'top-16 right-16 text-right',
  'bottom-left': 'bottom-32 left-16 text-left',
  'bottom-right': 'bottom-32 right-16 text-right',
}

const textTransitions: Record<string, Record<string, string>> = {
  'center': { enterFrom: 'opacity-0', enterTo: 'opacity-100' },
  'top-center': { enterFrom: 'opacity-0 translate-y-96', enterTo: 'opacity-100 translate-y-0' },
  'bottom-center': { enterFrom: 'opacity-0 -translate-y-96', enterTo: 'opacity-100 translate-y-0' },
  'top-left': { enterFrom: 'opacity-0 translate-y-96', enterTo: 'opacity-100 translate-y-0' },
  'top-right': { enterFrom: 'opacity-0 -translate-x-96', enterTo: 'opacity-100 translate-x-0' },
  'bottom-left': { enterFrom: 'opacity-0 -translate-y-96', enterTo: 'opacity-100 translate-y-0' },
  'bottom-right': { enterFrom: 'opacity-0 -translate-x-96', enterTo: 'opacity-100 translate-x-0' },
}

const lineHeights: Record<string, string> = {
  'font-tiempos': '!leading-[1.14]',
  'font-inter': '!leading-[1.2]',
  'font-calistoga': '!leading-tight',
  'font-playpen': '!leading-normal',
}

const isPlaying = ref(true)
const backgroundVideo = useTemplateRef('backgroundVideo')

const { t } = useI18n()
const { getSvgOrImage, getImageSizeClass } = useImage()
const { hasHeading } = useHiddenHeading()
const { findContents } = useContentApi()

const image = getSvgOrImage(
  props.page?.svg ?? { destinationContentId: 0 },
  props.page?.image ?? { destinationContentId: 0, uri: '' },
  'presentation_full'
)

const { data: video, isLoading: isLoadingVideo } = useQuery({
  enabled: computed(() => !!props.page.video?.destinationContentId),
  queryKey: ['video', props.page.video?.destinationContentId],
  queryFn: () => findContents<ContentVideo>({
    contentIdCriterion: [Number(props.page.video!.destinationContentId)],
    contentTypeCriterion: [ContentType.Video],
    subtreeCriterion: [Subtree.Content, Subtree.Media],
    mainLocationCriterion: true,
  }, 1).then(([data]) => data),
  staleTime: Infinity,
})

const colorPair = computed(() => props.page.colorPair)
const lottieId = computed(() => Number(props.page.lottie?.destinationContentId ?? 0))
const textPosition = computed(() => textPositions[props.page.textPlacement])
const transition = computed(() => textTransitions[props.page.textPlacement])

const headerClasses = computed(() => {
  return `${props.page.textSizes.heading} ${props.page.fontFamily} ${props.page.fontWeight} ${lineHeights[props.page.fontFamily]}`
})

const backgroundAndText = computed(() => ({
  backgroundColor: colorPair.value.background,
  color: colorPair.value.text,
}))

const togglePlay = () => {
  if (!backgroundVideo.value) {
    return
  }

  isPlaying.value = !isPlaying.value

  if (backgroundVideo.value.paused) {
    backgroundVideo.value.play()
  } else {
    backgroundVideo.value.pause()
  }
}
</script>

<template>
  <div
    :style="[backgroundAndText, { 'color': colorPair.text, '--hover-bg': colorPair.text, '--hover-color': colorPair.background }]"
    class="h-screen w-screen"
  >
    <h1
      v-if="!hasHeading"
      class="sr-only"
      v-text="page.title"
    />
    <template v-if="video?.metadata && !isLoadingVideo">
      <video
        ref="backgroundVideo"
        :poster="video.metadata.image"
        class="size-full object-cover object-center"
        disablePictureInPicture
        playsinline
        autoplay
        muted
        loop
      >
        <source
          :src="video.metadata.elementURI"
          :type="video.metadata.mimeType"
        >
      </video>
      <Teleport to="#slide-action" defer>
        <Button
          size="lg"
          shape="square"
          :aria-label="isPlaying ? t('media.pauseVideo') : t('media.playVideo')"
          variant="outline"
          :style="{ background: colorPair.background, color: colorPair.text, outlineColor: colorPair.text }"
          class="hover:not-disabled:!bg-(--hover-bg) hover:not-disabled:!text-(--hover-color)"
          @click="togglePlay"
        >
          <font-awesome-icon :icon="isPlaying ? faPause : faPlay" />
        </Button>
      </Teleport>
    </template>
    <LottiePlayer
      v-else-if="lottieId && !isLoadingVideo"
      :content-id="lottieId"
      :color-pair="colorPair"
    />
    <ImageWithCaption
      v-else-if="image && !isLoadingVideo"
      :image="image"
      :classes="`h-full w-full ${getImageSizeClass(page.imageSize)}`"
      :color-pair="page.colorPair"
    />
    <div
      v-if="page.header"
      class="absolute flex max-w-[calc(100vw-8rem)]"
      :class="textPosition"
    >
      <Transition
        enter-active-class="duration-1000 ease delay-1000"
        :enter-from-class="transition.enterFrom"
        :enter-to-class="transition.enterTo"
        appear
      >
        <h1 :class="headerClasses">
          <span
            :style="backgroundAndText"
            class="box-decoration-clone px-4"
            v-text="page.header"
          />
        </h1>
      </Transition>
    </div>
  </div>
</template>
